"use client";

import type { AiMarkerKind } from "@repo/ai-disclaimer";
import {
  assetThumbUrl,
  type InventoryRow,
} from "@repo/utils/aiImagesInventory";
import {
  AI_BADGE_POSITIONS,
  AI_BADGE_POSITION_LABEL,
  type AiBadgePosition,
} from "@repo/utils/aiBadgePosition";
import {
  AdminBadge,
  AdminCheckbox,
  AdminSelect,
  AdminSpinner,
} from "./AdminUi";

const KIND_LABEL: Record<AiMarkerKind, string> = {
  generated: "AI-generated",
  modified: "AI-edited",
  unspecified: "AI (unspecified)",
};

const KIND_VALUES: readonly AiMarkerKind[] = [
  "generated",
  "modified",
  "unspecified",
];

const AiImagesRow = ({
  row,
  selected,
  siteBadgePosition,
  badgePosition,
  pendingMarker,
  pendingPosition,
  locked,
  onToggle,
  onPreview,
  onMarker,
  onBadgePosition,
}: {
  row: InventoryRow;
  selected: boolean;
  /** Shown as the "Default" option's label, so an editor can see what it resolves to. */
  siteBadgePosition: AiBadgePosition;
  /** This asset's override, or null when it follows the site default. */
  badgePosition: AiBadgePosition | null;
  /** True while this row's marker write is in flight. */
  pendingMarker: boolean;
  /** True while this row's position write is in flight. */
  pendingPosition: boolean;
  /**
   * True while ANY write on the page is in flight, this row's or another's.
   *
   * Every write is a read-modify-write of the same config story, so a second one started
   * before the first returns reads stale content and writes the first edit back out of
   * existence. The page serialises them; this is what that looks like in a row.
   */
  locked: boolean;
  onToggle: (id: number) => void;
  onPreview: (row: InventoryRow) => void;
  onMarker: (id: number, kind: AiMarkerKind | null) => void;
  onBadgePosition: (id: number, position: AiBadgePosition | null) => void;
}) => {
  const busy = locked || pendingMarker || pendingPosition;
  /** Only a marked asset has a registry row for a corner to be stored against. */
  const markable = row.isAi && !!row.kind;

  return (
    <tr className="border-b border-[var(--admin-border)] align-top hover:bg-[var(--admin-surface-muted)]">
      <td className="p-2">
        <AdminCheckbox
          checked={selected}
          onChange={() => onToggle(row.id)}
          aria-label={`Select ${row.filename || `asset ${row.id}`}`}
        />
      </td>
      <td className="p-2">
        {row.url && row.isVideo ? (
          // The image service has no rendition for a video, so the poster frame the
          // browser pulls from the file itself is the only preview there is. Muted and
          // uncontrolled: this is a 96px identification thumbnail, not a player.
          <video
            src={row.url}
            muted
            playsInline
            preload="metadata"
            className="h-24 w-24 rounded-[var(--admin-radius)] border border-[var(--admin-border)] bg-[var(--admin-surface-muted)] object-cover"
            aria-label={`Preview of ${row.filename || `asset ${row.id}`}`}
          />
        ) : row.url ? (
          <button
            type="button"
            onClick={() => onPreview(row)}
            className="block h-24 w-24 cursor-pointer overflow-hidden rounded-[var(--admin-radius)] border border-[var(--admin-border)] bg-[var(--admin-surface-muted)] hover:border-[var(--admin-accent)]"
            aria-label={`Preview ${row.filename || `asset ${row.id}`}`}
          >
            {/* Plain <img>: next/image runs through a Storyblok loader tuned for page
                content, and this is an internal admin table of raw asset urls. */}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              // The box is 96px wide with a 1px border either side, so the image renders at
              // 94px. 188 is twice that — sharp on retina, and no wider than it needs to be.
              src={assetThumbUrl(row.url, 188)}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
          </button>
        ) : (
          <div
            className="flex h-24 w-24 items-center justify-center rounded-[var(--admin-radius)] border border-dashed border-[var(--admin-border-strong)] text-[10px] text-[var(--admin-text-faint)]"
            aria-hidden="true"
          >
            n/a
          </div>
        )}
      </td>
      <td className="max-w-xs p-2 font-mono text-xs break-all">
        {row.filename || (
          <span className="text-[var(--admin-text-faint)]">(no filename)</span>
        )}
        {row.isVideo && (
          <AdminBadge className="ml-2 align-middle">video</AdminBadge>
        )}
      </td>
      <td className="p-2 text-sm">
        {row.cleanAlt || (
          // Distinct from a marked-but-empty alt: 671 of 703 live assets have no alt at
          // all, so this is the common case and must not look like an error.
          <span className="text-[var(--admin-text-faint)]">(no alt text)</span>
        )}
      </td>
      <td className="p-2 text-sm whitespace-nowrap">
        <span className="inline-flex items-center gap-2">
          <AdminSelect
            value={row.kind ?? ""}
            disabled={busy}
            onChange={(event) =>
              onMarker(
                row.id,
                (event.target.value || null) as AiMarkerKind | null,
              )
            }
            aria-label={`Marker for ${row.filename || `asset ${row.id}`}`}
          >
            <option value="">Not AI</option>
            {KIND_VALUES.map((value) => (
              <option key={value} value={value}>
                {KIND_LABEL[value]}
              </option>
            ))}
          </AdminSelect>
          {pendingMarker && <AdminSpinner />}
        </span>
      </td>
      <td className="p-2 text-sm whitespace-nowrap">
        {/* Only a marked asset has a badge to place. An unmarked row shows nothing
            rather than a disabled control, because the position route refuses it: a
            registry row written for an unmarked asset would badge it. */}
        {markable ? (
          <span className="inline-flex items-center gap-2">
            <AdminSelect
              value={badgePosition ?? ""}
              disabled={busy}
              onChange={(event) =>
                onBadgePosition(
                  row.id,
                  (event.target.value || null) as AiBadgePosition | null,
                )
              }
              aria-label={`Badge position for ${row.filename || `asset ${row.id}`}`}
            >
              <option value="">
                Default ({AI_BADGE_POSITION_LABEL[siteBadgePosition]})
              </option>
              {AI_BADGE_POSITIONS.map((position) => (
                <option key={position} value={position}>
                  {AI_BADGE_POSITION_LABEL[position]}
                </option>
              ))}
            </AdminSelect>
            {pendingPosition && <AdminSpinner />}
          </span>
        ) : (
          <span className="text-[var(--admin-text-faint)]">—</span>
        )}
      </td>
    </tr>
  );
};

export default AiImagesRow;
